import { Head, useForm } from "@inertiajs/react"

import { DEFAULT_CODE_LANGUAGE } from "@/components/code-take-card"
import { useTimeZone } from "@/hooks/use-time-zone"
import AppLayout from "@/layouts/app-layout"
import { isoToZonedWallClock } from "@/lib/scheduling"
import { editStoryPath, storiesPath, storyPath } from "@/routes"
import type {
  BreadcrumbItem,
  CodeTake,
  MentionHandleGroup,
  Platform,
  PlatformCredentials,
  QuoteTake,
  Story,
  Take,
  YoutubeCategory,
} from "@/types"

import LongFormStoryForm from "./long-form-story-form"
import StoryForm, {
  type StoryFormData,
  type StoryTakeAttribute,
} from "./story-form"
import {
  emptyTakeAttributes,
  syndicationEntryKey,
  syndicationsAttributesFromStory,
} from "./story-form-utils"

// The serializer types take.type as a plain string, so narrow it back to the
// known take types here, falling back to "Take" for anything unrecognized.
const KNOWN_TAKE_TYPES: StoryTakeAttribute["type"][] = [
  "Take",
  "CodeTake",
  "QuoteTake",
]

function normalizedTakeType(type?: string | null): StoryTakeAttribute["type"] {
  return KNOWN_TAKE_TYPES.find((known) => known === type) ?? "Take"
}

function isCodeTake(take: Take | CodeTake | QuoteTake): take is CodeTake {
  return take.type === "CodeTake"
}

interface SharedEditProps {
  story: Story
  available_platforms: Platform[]
  platform_credentials: PlatformCredentials
  mention_handle_groups: MentionHandleGroup[]
  mention_platforms: Platform[]
  mastodon_server_domain?: string | null
  youtube_categories: YoutubeCategory[]
  default_youtube_category_id: number
}

function ShortFormEdit({
  story,
  available_platforms,
  platform_credentials,
  mention_handle_groups,
  mention_platforms,
  mastodon_server_domain,
  youtube_categories,
  default_youtube_category_id,
}: SharedEditProps) {
  const timeZone = useTimeZone()
  const { data, setData, patch, processing, errors, transform } = useForm<{
    story: StoryFormData
  }>({
    story: {
      title: story.title ?? "",
      tags: story.tags,
      scheduled_at: isoToZonedWallClock(story.scheduled_at, timeZone),
      syndications_attributes: syndicationsAttributesFromStory(story),
      takes_attributes:
        story.takes.length > 0
          ? story.takes.reduce<Record<number, StoryTakeAttribute>>(
              (attributes, take, index) => ({
                ...attributes,
                [index]: {
                  id: take.id,
                  type: normalizedTakeType(take.type),
                  body: take.body ?? "",
                  code_snippet: isCodeTake(take)
                    ? (take.code_snippet ?? "")
                    : "",
                  code_language: isCodeTake(take)
                    ? (take.code_language ?? DEFAULT_CODE_LANGUAGE)
                    : DEFAULT_CODE_LANGUAGE,
                  code_image_data_url: "",
                  quoted_take_id: take.quoted_take_id ?? null,
                  position: take.position,
                  take_media_files_attributes: {},
                },
              }),
              {},
            )
          : { 0: { ...emptyTakeAttributes(), position: 0 } },
    },
  })

  const lockedSyndications = story.syndications.filter(
    (syndication) => syndication.locked,
  )
  const lockedKeys = lockedSyndications.map((syndication) =>
    syndicationEntryKey(
      syndication.platform.name,
      syndication.platform_credential_id,
    ),
  )
  const lockedStatusLabels = Object.fromEntries(
    lockedSyndications.map((syndication) => [
      syndicationEntryKey(
        syndication.platform.name,
        syndication.platform_credential_id,
      ),
      syndication.status_label,
    ]),
  )

  function submit(value: StoryFormData, publish: boolean) {
    transform(() => ({ story: value, publish }))
    patch(storyPath(story.id), {
      onFinish: () => transform((current) => current),
    })
  }

  return (
    <StoryForm
      data={data.story}
      errors={(errors.story as unknown as Record<string, string>) ?? {}}
      processing={processing}
      existingTakes={story.takes}
      lockedKeys={lockedKeys}
      lockedStatusLabels={lockedStatusLabels}
      availablePlatforms={available_platforms}
      currentStoryId={story.id}
      platformCredentials={platform_credentials}
      mentionHandleGroups={mention_handle_groups}
      mentionPlatforms={mention_platforms}
      mastodonServerDomain={mastodon_server_domain}
      youtubeCategories={youtube_categories}
      defaultYoutubeCategoryId={default_youtube_category_id}
      onChange={(value) => setData("story", value)}
      onSubmit={(_e, value) => submit(value, false)}
      onPublish={(_e, value) => submit(value, true)}
      onCancel={() => window.history.back()}
    />
  )
}

function LongFormEdit({
  story,
  available_platforms,
  platform_credentials,
  youtube_categories,
  default_youtube_category_id,
}: SharedEditProps) {
  const existingTake = story.takes[0]
  const timeZone = useTimeZone()

  const { data, setData, patch, processing, errors, transform } = useForm<{
    story: StoryFormData
  }>({
    story: {
      title: story.title ?? "",
      tags: story.tags,
      scheduled_at: isoToZonedWallClock(story.scheduled_at, timeZone),
      syndications_attributes: syndicationsAttributesFromStory(story),
      takes_attributes: {
        0: {
          ...emptyTakeAttributes(),
          id: existingTake?.id,
          body: existingTake?.body ?? "",
          position: 0,
        },
      },
    },
  })

  function submit(value: StoryFormData, publish: boolean) {
    transform(() => ({ story: value, publish }))
    patch(storyPath(story.id), {
      onFinish: () => transform((current) => current),
    })
  }

  return (
    <LongFormStoryForm
      data={data.story}
      errors={(errors.story as unknown as Record<string, string>) ?? {}}
      processing={processing}
      availablePlatforms={available_platforms}
      platformCredentials={platform_credentials}
      youtubeCategories={youtube_categories}
      defaultYoutubeCategoryId={default_youtube_category_id}
      onChange={(value) => setData("story", value)}
      onSubmit={(_e, value) => submit(value, false)}
      onPublish={(_e, value) => submit(value, true)}
      onCancel={() => window.history.back()}
    />
  )
}

export default function StoriesEdit(props: SharedEditProps) {
  const { story } = props
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Newsroom", href: storiesPath() },
    { title: "Edit", href: editStoryPath(story.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Story" />

      <div className="flex flex-col gap-6 p-6">
        {story.long_form ? (
          <LongFormEdit {...props} />
        ) : (
          <ShortFormEdit {...props} />
        )}
      </div>
    </AppLayout>
  )
}
